import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { type ColumnDef } from '@tanstack/react-table';
import { toast } from 'sonner';
import { ChevronRight, Forklift, MoreHorizontal, Plus, Trash2 } from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card, CardContent } from '@/components/ui/Card';
import { EmptyState } from '@/components/ui/EmptyState';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Checkbox } from '@/components/ui/Checkbox';
import { DataTable, arrIncludesSome, type FacetedFilter } from '@/components/ui/DataTable';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/DropdownMenu';
import {
  EditableCategory,
  EditableCurrency,
  EditableSelect,
  EditableText,
} from '@/components/inventory/EditableItemField';
import { EquipmentThumbnail } from '@/components/equipment/EquipmentThumbnail';
import {
  DueDate,
  EquipmentConditionBadge,
  EquipmentStatusBadge,
  WarrantyBadge,
} from '@/components/equipment/EquipmentBadges';
import { SummaryCard } from '@/components/equipment/DetailRows';
import { NewEquipmentDialog } from '@/components/equipment/NewEquipmentDialog';
import {
  useDeleteEquipment,
  useEquipmentList,
  useUpdateEquipment,
  type EquipmentListRow,
} from '@/features/equipment/hooks';
import {
  EQUIPMENT_CATEGORY_META,
  EQUIPMENT_CATEGORY_OPTIONS,
  EQUIPMENT_STATUS_OPTIONS,
  canEditEquipment,
  dueState,
  equipmentCategoryLabel,
  getEquipmentStatusMeta,
  isEquipmentCategory,
  type EquipmentCategory,
} from '@/features/equipment/model';
import { useAuth } from '@/app/AuthProvider';
import { formatCurrency, formatDateOnly } from '@/lib/formatters';

const SHOW_RETIRED_KEY = 'catalog-equipment-show-retired';

function isRetired(row: EquipmentListRow): boolean {
  return row.status === 'retired' || !!row.disposed_on;
}

function makeModel(row: EquipmentListRow): string {
  return [row.manufacturer, row.model].filter(Boolean).join(' ');
}

/**
 * Equipment register grid. Rendered for every category at
 * `/catalog/equipment` and per category at
 * `/catalog/equipment/category/:category`, which hides the redundant
 * Category column and pre-selects that category in the add dialog.
 */
export function EquipmentPage({ category }: { category?: EquipmentCategory } = {}) {
  const navigate = useNavigate();
  const { role } = useAuth();
  const canEdit = canEditEquipment(role);
  const { data, isLoading } = useEquipmentList(category ? { category } : undefined);
  const updateEquipment = useUpdateEquipment();
  const deleteEquipment = useDeleteEquipment();
  const [newOpen, setNewOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<{
    rows: EquipmentListRow[];
    clearSelection: () => void;
  } | null>(null);

  const [showRetired, setShowRetired] = useState(() => {
    try {
      return window.localStorage.getItem(SHOW_RETIRED_KEY) === '1';
    } catch {
      return false;
    }
  });
  useEffect(() => {
    try {
      window.localStorage.setItem(SHOW_RETIRED_KEY, showRetired ? '1' : '0');
    } catch {
      // Private mode can reject storage; the in-memory choice still applies.
    }
  }, [showRetired]);

  const allRows = useMemo<EquipmentListRow[]>(() => data ?? [], [data]);
  const rows = useMemo(
    () => (showRetired ? allRows : allRows.filter((r) => !isRetired(r))),
    [allRows, showRetired],
  );
  const retiredCount = useMemo(() => allRows.filter(isRetired).length, [allRows]);

  const totals = useMemo(() => {
    const active = allRows.filter((r) => !isRetired(r));
    let cost = 0;
    let book = 0;
    let overdue = 0;
    let dueSoon = 0;
    for (const r of active) {
      cost += r.purchase_price_cents ?? 0;
      book += r.book_value_cents ?? 0;
      const state = dueState(r.next_maintenance_on);
      if (state === 'overdue') overdue += 1;
      if (state === 'due_soon') dueSoon += 1;
    }
    return { count: active.length, cost, book, overdue, dueSoon };
  }, [allRows]);

  const typeOptions = useMemo(() => {
    const seen = new Set<string>(category ? EQUIPMENT_CATEGORY_META[category].typeSuggestions : []);
    for (const r of allRows) if (r.equipment_type) seen.add(r.equipment_type);
    return [...seen].sort((a, b) => a.localeCompare(b));
  }, [allRows, category]);

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    const { rows: targets, clearSelection } = pendingDelete;
    try {
      await deleteEquipment.mutateAsync({ ids: targets.map((r) => r.id) });
      toast.success(
        targets.length === 1 ? `Deleted ${targets[0].asset_tag}` : `Deleted ${targets.length} assets`,
      );
      clearSelection();
      setPendingDelete(null);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not delete equipment.');
    }
  };

  const columns = useMemo<ColumnDef<EquipmentListRow>[]>(() => {
    const save = (id: string, patch: Parameters<typeof updateEquipment.mutateAsync>[0]['patch']) =>
      updateEquipment.mutateAsync({ id, patch }).then(() => undefined);

    const cols: ColumnDef<EquipmentListRow>[] = [
      {
        id: '__thumbnail',
        enableSorting: false,
        enableHiding: false,
        header: () => null,
        size: 56,
        minSize: 56,
        cell: ({ row }) => (
          <EquipmentThumbnail imagePath={row.original.image_path} category={row.original.category} />
        ),
      },
      {
        id: 'asset_tag',
        accessorKey: 'asset_tag',
        header: 'Asset tag',
        size: 120,
        minSize: 90,
        cell: ({ row }) => {
          const e = row.original;
          if (!canEdit) {
            return (
              <Link
                to={`/catalog/equipment/${e.id}`}
                onClick={(ev) => ev.stopPropagation()}
                className="whitespace-nowrap font-mono text-xs text-foreground hover:underline"
              >
                {e.asset_tag}
              </Link>
            );
          }
          return (
            <EditableText
              value={e.asset_tag}
              variant="mono"
              required
              maxLength={64}
              ariaLabel="Asset tag"
              canEdit
              density="cell"
              onSave={(next) => save(e.id, { asset_tag: next })}
            />
          );
        },
      },
      {
        id: 'name',
        accessorKey: 'name',
        header: 'Name',
        size: 220,
        minSize: 120,
        cell: ({ row }) => {
          const e = row.original;
          if (!canEdit) {
            return (
              <Link
                to={`/catalog/equipment/${e.id}`}
                onClick={(ev) => ev.stopPropagation()}
                className="font-medium text-foreground hover:underline"
              >
                {e.name}
              </Link>
            );
          }
          return (
            <EditableText
              value={e.name}
              required
              maxLength={200}
              ariaLabel="Name"
              canEdit
              density="cell"
              triggerClassName="font-medium"
              onSave={(next) => save(e.id, { name: next })}
            />
          );
        },
      },
      {
        id: 'category',
        accessorKey: 'category',
        header: 'Category',
        size: 160,
        minSize: 110,
        filterFn: arrIncludesSome,
        cell: ({ row }) => {
          const e = row.original;
          return (
            <EditableSelect
              value={e.category}
              options={EQUIPMENT_CATEGORY_OPTIONS}
              ariaLabel="Category"
              canEdit={canEdit}
              density="cell"
              triggerClassName="min-w-[6.75rem]"
              display={
                <span>
                  {isEquipmentCategory(e.category)
                    ? EQUIPMENT_CATEGORY_META[e.category].shortLabel
                    : equipmentCategoryLabel(e.category)}
                </span>
              }
              onSave={(next) => save(e.id, { category: next })}
            />
          );
        },
      },
      {
        id: 'equipment_type',
        accessorFn: (r) => r.equipment_type ?? '',
        header: 'Type',
        size: 160,
        minSize: 100,
        filterFn: arrIncludesSome,
        cell: ({ row }) => {
          const e = row.original;
          return (
            <EditableCategory
              value={e.equipment_type}
              options={typeOptions}
              ariaLabel="Type"
              canEdit={canEdit}
              density="cell"
              placeholder="Type"
              onSave={(next) => save(e.id, { equipment_type: next })}
            />
          );
        },
      },
      {
        id: 'make_model',
        accessorFn: makeModel,
        header: 'Make / model',
        size: 180,
        minSize: 100,
        cell: ({ row }) => {
          const text = makeModel(row.original);
          return text ? (
            <span className="text-sm">{text}</span>
          ) : (
            <span className="text-xs text-muted-foreground">—</span>
          );
        },
      },
      {
        id: 'serial_number',
        accessorFn: (r) => r.serial_number ?? '',
        header: 'Serial',
        size: 140,
        minSize: 90,
        cell: ({ row }) =>
          row.original.serial_number ? (
            <span className="font-mono text-xs">{row.original.serial_number}</span>
          ) : (
            <span className="text-xs text-muted-foreground">—</span>
          ),
      },
      {
        id: 'location',
        accessorFn: (r) => r.location?.name ?? '',
        header: 'Location',
        size: 150,
        minSize: 100,
        filterFn: arrIncludesSome,
        cell: ({ row }) => {
          const e = row.original;
          if (!e.location && !e.location_detail) {
            return <span className="text-xs text-muted-foreground">—</span>;
          }
          return (
            <span className="text-sm">
              {e.location?.name}
              {e.location_detail && (
                <span className="text-muted-foreground">
                  {e.location ? ' · ' : ''}
                  {e.location_detail}
                </span>
              )}
            </span>
          );
        },
      },
      {
        id: 'assignee',
        accessorFn: (r) => r.assignee?.full_name ?? r.assignee?.email ?? '',
        header: 'Assigned to',
        size: 150,
        minSize: 100,
        filterFn: arrIncludesSome,
        cell: ({ row }) => {
          const a = row.original.assignee;
          return a ? (
            <span className="text-sm">{a.full_name ?? a.email}</span>
          ) : (
            <span className="text-xs text-muted-foreground">—</span>
          );
        },
      },
      {
        id: 'status',
        accessorKey: 'status',
        header: 'Status',
        size: 160,
        minSize: 110,
        filterFn: arrIncludesSome,
        cell: ({ row }) => {
          const e = row.original;
          return (
            <EditableSelect
              value={e.status}
              options={EQUIPMENT_STATUS_OPTIONS}
              ariaLabel="Status"
              canEdit={canEdit}
              density="cell"
              display={<EquipmentStatusBadge status={e.status} />}
              onSave={(next) => save(e.id, { status: next })}
            />
          );
        },
      },
      {
        id: 'condition',
        accessorFn: (r) => r.condition ?? '',
        header: 'Condition',
        size: 110,
        minSize: 90,
        cell: ({ row }) => <EquipmentConditionBadge condition={row.original.condition} />,
      },
      {
        id: 'acquired_at',
        accessorFn: (r) => r.acquired_at ?? '',
        header: 'Purchased',
        size: 120,
        minSize: 90,
        cell: ({ row }) => (
          <span className="whitespace-nowrap tabular-nums text-sm">
            {formatDateOnly(row.original.acquired_at)}
          </span>
        ),
      },
      {
        id: 'purchase_price_cents',
        accessorKey: 'purchase_price_cents',
        header: () => <span className="block text-right">Purchase price</span>,
        size: 130,
        minSize: 90,
        sortingFn: 'basic',
        cell: ({ row }) => {
          const e = row.original;
          return (
            <EditableCurrency
              value={e.purchase_price_cents}
              ariaLabel="Purchase price"
              canEdit={canEdit}
              density="cell"
              align="right"
              triggerClassName="justify-end"
              onSave={(next) => save(e.id, { purchase_price_cents: next })}
            />
          );
        },
      },
      {
        id: 'book_value_cents',
        accessorFn: (r) => r.book_value_cents ?? -1,
        header: () => <span className="block text-right">Book value</span>,
        size: 120,
        minSize: 90,
        sortingFn: 'basic',
        cell: ({ row }) => (
          <span className="block text-right tabular-nums text-muted-foreground">
            {formatCurrency(row.original.book_value_cents)}
          </span>
        ),
      },
      {
        id: 'next_maintenance_on',
        accessorFn: (r) => r.next_maintenance_on ?? '9999-12-31',
        header: 'Next maintenance',
        size: 150,
        minSize: 120,
        cell: ({ row }) => <DueDate date={row.original.next_maintenance_on} className="text-sm" />,
      },
      {
        id: 'warranty_expires_on',
        accessorFn: (r) => r.warranty_expires_on ?? '',
        header: 'Warranty',
        size: 150,
        minSize: 100,
        cell: ({ row }) => <WarrantyBadge expiresOn={row.original.warranty_expires_on} />,
      },
      {
        id: '__open',
        enableSorting: false,
        enableHiding: false,
        header: () => null,
        size: 32,
        cell: ({ row }) => (
          <Link
            to={`/catalog/equipment/${row.original.id}`}
            onClick={(e) => e.stopPropagation()}
            aria-label="Open equipment"
            className="text-muted-foreground"
          >
            <ChevronRight className="h-4 w-4" />
          </Link>
        ),
      },
    ];

    if (canEdit) {
      cols.push({
        id: '__actions',
        enableSorting: false,
        enableHiding: false,
        header: () => null,
        size: 44,
        minSize: 44,
        cell: ({ row }) => {
          const e = row.original;
          return (
            <div onClick={(ev) => ev.stopPropagation()}>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 text-muted-foreground"
                    aria-label={`Actions for ${e.name}`}
                  >
                    <MoreHorizontal className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem
                    className="text-destructive focus:bg-destructive/10 focus:text-destructive"
                    onSelect={() => setPendingDelete({ rows: [e], clearSelection: () => {} })}
                  >
                    <Trash2 className="mr-2 h-4 w-4" />
                    Delete
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          );
        },
      });
    }

    return category ? cols.filter((c) => c.id !== 'category') : cols;
  }, [canEdit, category, typeOptions, updateEquipment]);

  const facetedFilters: FacetedFilter[] = useMemo(() => {
    const filters: FacetedFilter[] = [];
    if (!category) {
      filters.push({ columnId: 'category', title: 'Category', options: EQUIPMENT_CATEGORY_OPTIONS });
    }
    filters.push(
      { columnId: 'equipment_type', title: 'Type' },
      { columnId: 'location', title: 'Location' },
      {
        columnId: 'status',
        title: 'Status',
        options: showRetired
          ? EQUIPMENT_STATUS_OPTIONS
          : EQUIPMENT_STATUS_OPTIONS.filter((o) => o.value !== 'retired'),
      },
      { columnId: 'assignee', title: 'Assigned to' },
    );
    return filters;
  }, [category, showRetired]);

  const meta = category ? EQUIPMENT_CATEGORY_META[category] : null;
  const title = meta?.label ?? 'Equipment';
  const tableId = category ? `catalog-equipment-${category}` : 'catalog-equipment';

  return (
    <div className="space-y-6">
      <PageHeader
        title={title}
        eyebrow={category ? 'Catalog / Equipment' : undefined}
        actions={
          canEdit ? (
            <Button onClick={() => setNewOpen(true)}>
              <Plus className="h-4 w-4" />
              Add equipment
            </Button>
          ) : undefined
        }
      />

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <SummaryCard label="Assets" value={totals.count.toLocaleString()} caption="Excludes retired" />
        <SummaryCard
          label="Purchase cost"
          value={formatCurrency(totals.cost, { whole: true })}
        />
        <SummaryCard
          label="Book value today"
          value={formatCurrency(totals.book, { whole: true })}
        />
        <SummaryCard
          label="Maintenance due"
          value={(totals.overdue + totals.dueSoon).toLocaleString()}
          tone={totals.overdue > 0 ? 'warning' : 'neutral'}
          caption={
            totals.overdue > 0 ? `${totals.overdue} overdue` : 'Within the next 14 days'
          }
        />
      </div>

      <Card>
        <CardContent className="pt-6">
          <DataTable<EquipmentListRow, unknown>
            data={rows}
            columns={columns}
            isLoading={isLoading}
            getRowId={(r) => r.id}
            searchPlaceholder="Search by tag, name, make or serial…"
            facetedFilters={facetedFilters}
            initialPageSize={25}
            pageSizes={[10, 25, 50, 100, 200]}
            onRowClick={(r) => navigate(`/catalog/equipment/${r.id}`)}
            rowClassName={(r) => (isRetired(r) ? 'opacity-60' : undefined)}
            initialColumnVisibility={{
              serial_number: false,
              condition: false,
              assignee: category === 'office',
            }}
            toolbarSlot={
              <label className="inline-flex min-h-11 cursor-pointer items-center gap-2 rounded-md border border-border px-3 text-sm">
                <Checkbox
                  checked={showRetired}
                  onCheckedChange={setShowRetired}
                  ariaLabel="Show retired equipment"
                />
                Show retired
                {!showRetired && retiredCount > 0 && (
                  <span className="tabular-nums text-muted-foreground">({retiredCount})</span>
                )}
              </label>
            }
            enableRowSelection={canEdit}
            bulkActions={
              canEdit
                ? (table) => (
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() =>
                        setPendingDelete({
                          rows: table.getSelectedRowModel().rows.map((r) => r.original),
                          clearSelection: () => table.resetRowSelection(),
                        })
                      }
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                      Delete
                    </Button>
                  )
                : undefined
            }
            enableColumnReordering
            enableColumnResizing
            tableId={tableId}
            mobileCard={{
              leading: (r) => (
                <EquipmentThumbnail imagePath={r.image_path} category={r.category} size="md" />
              ),
              title: (r) => r.name,
              subtitle: (r) => (
                <span>
                  <span className="font-mono text-[11px]">{r.asset_tag}</span>
                  {r.equipment_type && <span> · {r.equipment_type}</span>}
                </span>
              ),
              dimmed: isRetired,
              badges: (r) => {
                const status = getEquipmentStatusMeta(r.status);
                const out: React.ReactNode[] = [
                  <Badge key="status" variant={status.variant}>
                    {status.label}
                  </Badge>,
                ];
                if (!category) {
                  out.push(
                    <Badge key="category" variant="outline">
                      {equipmentCategoryLabel(r.category)}
                    </Badge>,
                  );
                }
                const due = dueState(r.next_maintenance_on);
                if (due === 'overdue') {
                  out.push(
                    <Badge key="due" variant="warning">
                      Maintenance overdue
                    </Badge>,
                  );
                } else if (due === 'due_soon') {
                  out.push(
                    <Badge key="due" variant="info-soft">
                      Maintenance due soon
                    </Badge>,
                  );
                }
                return out;
              },
              amount: (r) =>
                r.book_value_cents != null
                  ? { value: formatCurrency(r.book_value_cents), caption: 'Book value', tone: 'neutral' }
                  : null,
              meta: (r) => {
                const m: { label: string; value: React.ReactNode }[] = [];
                if (r.location) m.push({ label: 'Location', value: r.location.name });
                if (r.next_maintenance_on) {
                  m.push({ label: 'Next service', value: formatDateOnly(r.next_maintenance_on) });
                }
                if (r.purchase_price_cents != null) {
                  m.push({ label: 'Cost', value: formatCurrency(r.purchase_price_cents) });
                }
                if (r.assignee) m.push({ label: 'Assigned', value: r.assignee.full_name ?? r.assignee.email });
                return m;
              },
            }}
            emptyState={
              !showRetired && rows.length === 0 && retiredCount > 0 ? (
                <EmptyState
                  icon={Forklift}
                  title="Retired equipment is hidden"
                  action={
                    <Button variant="outline" onClick={() => setShowRetired(true)}>
                      Show retired
                    </Button>
                  }
                />
              ) : (
                <EmptyState
                  icon={meta?.icon ?? Forklift}
                  title={meta ? `No ${meta.label.toLowerCase()} yet` : 'No equipment yet'}
                  action={
                    canEdit ? (
                      <Button onClick={() => setNewOpen(true)}>
                        <Plus className="h-4 w-4" />
                        Add equipment
                      </Button>
                    ) : undefined
                  }
                />
              )
            }
          />
        </CardContent>
      </Card>

      <NewEquipmentDialog open={newOpen} onOpenChange={setNewOpen} defaultCategory={category} />

      <Dialog
        open={pendingDelete !== null}
        onOpenChange={(open) => {
          if (!open && !deleteEquipment.isPending) setPendingDelete(null);
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {pendingDelete?.rows.length === 1
                ? 'Delete equipment?'
                : `Delete ${pendingDelete?.rows.length ?? 0} assets?`}
            </DialogTitle>
            <DialogDescription>
              {pendingDelete?.rows.length === 1 ? (
                <>
                  This permanently removes{' '}
                  <span className="font-medium text-foreground">
                    {pendingDelete.rows[0].asset_tag} · {pendingDelete.rows[0].name}
                  </span>{' '}
                  with its photos, documents and maintenance history. To keep the history, set
                  the status to Retired instead.
                </>
              ) : (
                <>
                  This permanently removes the selected equipment with its photos, documents and
                  maintenance history. To keep the history, set the status to Retired instead.
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setPendingDelete(null)}
              disabled={deleteEquipment.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={confirmDelete}
              disabled={deleteEquipment.isPending}
            >
              {deleteEquipment.isPending ? 'Deleting…' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
